/**
 * Bulle — un message du fil (famille « conversation », 01-11-2026).
 *
 *   · `entrant` (le fan) et `sortant` (la créatrice, le chatteur, Cyrano) À GAUCHE, avatar devant ;
 *     le sortant a un voile plus clair. `disposition="deux-cotes"` (ou celle du `Fil`) met le sortant
 *     à droite, comme une messagerie de téléphone. `note` (une note interne : le fan ne la lit pas)
 *     au centre, en pointillés ; `systeme` rend un `Evenement` centré.
 *   · Les bulles consécutives d'un auteur se groupent : `position` (cf `grouperLesBulles`) resserre
 *     le coin qui touche la voisine ; l'`auteur` ne s'écrit qu'au-dessus de la première.
 *   · En bas à droite, comme une messagerie : « modifié », l'heure, le `statut` (en attente, envoyé,
 *     lu, échec, supprimé) ; le texte lui fait de la place sur sa dernière ligne.
 *   · `traduction` : le texte est déjà dans la langue du chatteur ; le drapeau dit celle d'origine,
 *     « VO » ouvre l'original sous la bulle (Kayla, 26-09 : « ne jamais afficher le message dans la
 *     langue d'origine » — il reste à un clic).
 *   · `media` : un emplacement (la vignette, le verrou payant, le vocal — famille médias) ;
 *     `boutons` sous la bulle (`BoutonsDuMessage`) ; `reactions` dessous.
 *   · Au survol (ou au focus) : la barre d'outils — `reactionsRapides`, `actions`, et « … » qui
 *     ouvre `menu` (le `Menu` de la librairie). Le clic droit et l'appui long ouvrent le même menu ;
 *     au clavier, Maj+F10 ou la touche Menu.
 *
 *   <Bulle sens="entrant" position="premiere" heure="14:32" traduction={{ langue: "en", original: "what u doing tonight?" }}
 *          menu={[{ label: "Répondre", onClick: … }]}>
 *     <TexteRiche texte="Tu fais quoi ce soir ?" />
 *   </Bulle>
 */
import { useContext, useRef, useState, type KeyboardEvent, type ReactNode } from "react";

import { Avatar } from "../Avatar";
import { Button } from "../Button";
import { useMenu, type EntreeMenu } from "../Menu";
import { EyeOff, MoreHorizontal } from "../icons";
import { cn } from "../utils";
import { Citation, type CitationProps } from "./Citation";
import { ContexteDisposition, type DispositionDuFil } from "./disposition";
import { DrapeauDeLangue } from "./DrapeauDeLangue";
import { Reactions, ReactionsRapides, type Reaction } from "./Reactions";
import { type IdentiteAvatar } from "./Presence";
import { Evenement, type TonEvenement } from "./Reperes";
import { StatutDuMessage, type StatutMessage } from "./StatutDuMessage";
import type { PositionDansLeGroupe } from "./grouper";
import "./conversation.css";

export type SensBulle = "entrant" | "sortant" | "note" | "systeme";

/** Une action de la barre d'outils au survol : une icône, son nom. */
export interface ActionDeBulle {
  icone: ReactNode;
  libelle: string;
  onClick: () => void;
  danger?: boolean;
}

export interface LibellesBulle {
  note: string;
  modifie: string;
  supprime: string;
  reessayer: string;
  echec: string;
  vo: string;
  voOuvrir: string;
  voFermer: string;
  plus: string;
  outils: string;
}

export const LIBELLES_BULLE: LibellesBulle = {
  note: "Note interne",
  modifie: "modifié",
  supprime: "Message supprimé",
  reessayer: "Réessayer",
  echec: "Pas envoyé.",
  vo: "VO",
  voOuvrir: "Voir le message d'origine",
  voFermer: "Masquer le message d'origine",
  plus: "Plus d'actions",
  outils: "Actions du message",
};

export interface TraductionDeBulle {
  /** La langue d'origine (ISO 639-1) : son drapeau. */
  langue?: string | null;
  /** Le message d'origine — « VO » l'ouvre sous la bulle. Absent : le drapeau seul. */
  original?: ReactNode;
}

export interface BulleProps {
  sens: SensBulle;
  /** Le contenu : un texte (`TexteRiche`) ou rien quand la bulle n'est qu'un média. */
  children?: ReactNode;
  /** Sa place dans le groupe de l'auteur. Défaut `seule`. */
  position?: PositionDansLeGroupe;
  /** Au-dessus de la bulle : « Cyrano », « farieh », le nom d'un membre d'un groupe. */
  auteur?: ReactNode;
  /** À côté de l'auteur : des `Badge` (« BOT », le geste joué). */
  etiquettes?: ReactNode;
  /** L'auteur à côté de la bulle — l'`Avatar` de la librairie (sa photo, sa graine, son nom). */
  avatar?: IdentiteAvatar;
  /** Garde la place de l'avatar sans le montrer (les autres bulles du groupe). */
  avecAvatar?: boolean;
  /** « 14:32 » — déjà formaté. */
  heure?: string;
  /** L'instant, pour `<time dateTime>`. */
  dateTime?: string;
  statut?: StatutMessage;
  /** Ce que dit le statut (déjà traduit). Défaut : `LIBELLES_STATUT`. */
  libelleStatut?: string;
  /** Le message a été modifié. */
  modifie?: boolean;
  /** Avant l'heure, dans la méta : des vues, des transferts (« 👁 1,2 k »). */
  infos?: ReactNode;
  traduction?: TraductionDeBulle;
  /** La transcription d'un vocal, en italique sous le média (« « … » »). */
  transcription?: ReactNode;
  /** Le message auquel celui-ci répond. */
  citation?: CitationProps;
  /** Un média en tête de bulle (vignette, verrou payant, lecteur vocal — famille médias). */
  media?: ReactNode;
  /** Sous la bulle : `BoutonsDuMessage`. */
  boutons?: ReactNode;
  reactions?: readonly Reaction[];
  /** Poser ou retirer la nôtre en cliquant une réaction. */
  onReaction?: (reaction: Reaction) => void;
  /** La rangée d'emojis de la barre d'outils. */
  reactionsRapides?: { emojis?: readonly string[]; actuelle?: string | null; onChoisir: (emoji: string | null) => void };
  /** Les boutons de la barre d'outils : répondre, modifier, supprimer. */
  actions?: readonly ActionDeBulle[];
  /** Le menu du message (clic droit, appui long, « … ») — calculé à l'ouverture si c'est une fonction. */
  menu?: readonly EntreeMenu[] | (() => EntreeMenu[]);
  /** Renvoyer un message en échec : « Pas envoyé. Réessayer » sous la bulle. */
  onReessayer?: () => void;
  /** La bulle qu'on vient de rejoindre (citation cliquée, recherche) : un halo à la marque. */
  surlignee?: boolean;
  /** Le ton d'un message `systeme`. */
  ton?: TonEvenement;
  /** L'icône d'un message `systeme`. */
  icone?: ReactNode;
  libelles?: Partial<LibellesBulle>;
  /** Tout à gauche (défaut) ou le sortant à droite. Défaut : celle du `Fil` qui la contient. */
  disposition?: DispositionDuFil;
  /** L'ancre du message (`id` du DOM), pour y revenir. */
  id?: string;
  className?: string;
}

/** Un bouton de la barre d'outils : un cran plus petit sur un téléphone. */
const OUTIL = "h-[30px] w-[30px] sm:h-8 sm:w-8 [&_svg]:h-[17px] [&_svg]:w-[17px] sm:[&_svg]:h-[18px] sm:[&_svg]:w-[18px]";

export function Bulle(props: BulleProps) {
  if (props.sens === "systeme") {
    return (
      <div id={props.id} className={cn("cv-message", props.className)} data-sens="systeme">
        <Evenement ton={props.ton} icone={props.icone} heure={props.heure}>
          {props.children}
        </Evenement>
      </div>
    );
  }
  return <BulleDeMessage {...props} />;
}

function BulleDeMessage({
  sens,
  children,
  position = "seule",
  auteur,
  etiquettes,
  avatar,
  avecAvatar = false,
  heure,
  dateTime,
  statut,
  libelleStatut,
  modifie = false,
  infos,
  traduction,
  transcription,
  citation,
  media,
  boutons,
  reactions,
  onReaction,
  reactionsRapides,
  actions,
  menu,
  onReessayer,
  surlignee = false,
  libelles,
  disposition,
  id,
  className,
}: BulleProps) {
  const l = { ...LIBELLES_BULLE, ...libelles };
  const dispositionDuFil = useContext(ContexteDisposition);
  const aDroite = sens === "sortant" && (disposition ?? dispositionDuFil) === "deux-cotes";
  const alignementDuMenu = aDroite ? "droite" : "gauche";
  const [voOuverte, setVoOuverte] = useState(false);
  const bulle = useRef<HTMLDivElement>(null);
  const appui = useRef<number | null>(null);
  const { ouvrir, ouvrirSous, ouvert, menu: menuOuvert } = useMenu();

  const entrees = (): EntreeMenu[] => (typeof menu === "function" ? menu() : [...(menu ?? [])]);
  const avecMenu = menu !== undefined;
  const avecOutils = avecMenu || (actions?.length ?? 0) > 0 || reactionsRapides !== undefined;

  const supprime = statut === "supprime";
  const contenu = supprime && (children === undefined || children === null || children === "") ? l.supprime : children;
  const aDuTexte = contenu !== undefined && contenu !== null && contenu !== false && contenu !== "";
  const mediaSeul = media !== undefined && media !== null && !aDuTexte && !citation && !transcription && sens !== "note";
  const avecTraduction = traduction !== undefined && (traduction.langue || traduction.original !== undefined);
  const avecMeta = Boolean(heure || statut || modifie || infos || avecTraduction);
  const avecColonneAvatar = avatar !== undefined || avecAvatar;
  const auteurVisible = auteur !== undefined && auteur !== null;

  const meta = (interactif: boolean) => (
    <>
      {infos}
      {avecTraduction && <DrapeauDeLangue langue={traduction!.langue} taille={15} />}
      {avecTraduction && traduction!.original !== undefined && (
        // la même dans la place réservée (inerte) et dans le coin : elles font la même largeur
        <Button
          variant="ghost"
          size="sm"
          className="h-5 px-1.5 text-[11px] tracking-[0.04em]"
          aria-expanded={interactif ? voOuverte : undefined}
          title={voOuverte ? l.voFermer : l.voOuvrir}
          tabIndex={interactif ? undefined : -1}
          onClick={(e) => {
            e.stopPropagation();
            setVoOuverte((v) => !v);
          }}
        >
          {l.vo}
        </Button>
      )}
      {modifie && <span>{l.modifie}</span>}
      {heure && (interactif ? <time dateTime={dateTime}>{heure}</time> : <span>{heure}</span>)}
      {statut && <StatutDuMessage statut={statut} libelle={libelleStatut} />}
    </>
  );

  const annulerAppui = () => {
    if (appui.current !== null) {
      window.clearTimeout(appui.current);
      appui.current = null;
    }
  };

  const surClavier = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!avecMenu || e.target !== bulle.current) return;
    if (e.key === "ContextMenu" || (e.key === "F10" && e.shiftKey)) {
      e.preventDefault();
      if (bulle.current) ouvrirSous(bulle.current, entrees(), { alignement: alignementDuMenu });
    }
  };

  return (
    <div
      id={id}
      className={cn("cv-message", className)}
      data-sens={sens}
      data-cote={aDroite ? "droite" : undefined}
      data-debut-de-groupe={position === "seule" || position === "premiere" || undefined}
      data-outils-ouverts={ouvert || undefined}
    >
      {avecColonneAvatar && <div className="cv-avatar">{avatar && <Avatar {...avatar} taille={32} />}</div>}
      <div className="cv-colonne">
        {auteurVisible && (
          <div className="cv-auteur">
            <span>{auteur}</span>
            {etiquettes}
          </div>
        )}
        <div
          ref={bulle}
          className="cv-bulle"
          data-position={position}
          data-media-seul={mediaSeul || undefined}
          data-en-attente={statut === "en_attente" || undefined}
          data-echec={statut === "echec" || undefined}
          data-supprime={supprime || undefined}
          data-surlignee={surlignee || undefined}
          data-sens={sens === "note" ? "note" : undefined}
          tabIndex={avecOutils ? 0 : undefined}
          aria-haspopup={avecMenu ? "menu" : undefined}
          aria-keyshortcuts={avecMenu ? "Shift+F10" : undefined}
          onKeyDown={surClavier}
          onContextMenu={
            avecMenu
              ? (e) => {
                  annulerAppui();
                  ouvrir(e, entrees());
                }
              : undefined
          }
          onPointerDown={
            avecMenu
              ? (e) => {
                  if (e.pointerType !== "touch") return;
                  annulerAppui();
                  appui.current = window.setTimeout(() => {
                    appui.current = null;
                    if (bulle.current) ouvrirSous(bulle.current, entrees(), { alignement: alignementDuMenu });
                  }, 500);
                }
              : undefined
          }
          onPointerUp={avecMenu ? annulerAppui : undefined}
          onPointerCancel={avecMenu ? annulerAppui : undefined}
          onPointerLeave={avecMenu ? annulerAppui : undefined}
        >
          {sens === "note" && (
            <div className="cv-note-titre">
              <EyeOff aria-hidden />
              {l.note}
            </div>
          )}
          {citation && <Citation {...citation} />}
          {media !== undefined && media !== null && <div className="cv-media">{media}</div>}
          {(aDuTexte || transcription || (avecMeta && !mediaSeul)) && (
            <div className="cv-corps">
              {aDuTexte && contenu}
              {transcription && <span className="cv-transcription">{transcription}</span>}
              {voOuverte && traduction?.original !== undefined && (
                <span className="cv-original" lang={traduction.langue ?? undefined}>
                  {traduction.original}
                </span>
              )}
              {avecMeta && (
                <span className="cv-meta-place" aria-hidden inert>
                  {meta(false)}
                </span>
              )}
            </div>
          )}
          {avecMeta && <span className="cv-meta">{meta(true)}</span>}
        </div>
        {boutons}
        {reactions && reactions.length > 0 && <Reactions reactions={reactions} onBasculer={onReaction} />}
        {statut === "echec" && onReessayer && (
          <div className="cv-echec" role="alert">
            <span>{l.echec}</span>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-[12px]" onClick={onReessayer}>
              {l.reessayer}
            </Button>
          </div>
        )}
        {avecOutils && (
          <div className="cv-outils" role="toolbar" aria-label={l.outils}>
            {reactionsRapides && (
              <ReactionsRapides nue emojis={reactionsRapides.emojis} actuelle={reactionsRapides.actuelle} onChoisir={reactionsRapides.onChoisir} />
            )}
            {reactionsRapides && ((actions?.length ?? 0) > 0 || avecMenu) && <span className="cv-outils-trait" aria-hidden />}
            {actions?.map((a) => (
              <Button
                key={a.libelle}
                variant="ghost"
                size="lg"
                iconOnly
                className={cn(OUTIL, a.danger && "hover:text-[var(--color-erreur)]")}
                aria-label={a.libelle}
                title={a.libelle}
                onClick={a.onClick}
              >
                {a.icone}
              </Button>
            ))}
            {avecMenu && (
              <Button
                variant="ghost"
                size="lg"
                iconOnly
                className={OUTIL}
                aria-label={l.plus}
                title={l.plus}
                aria-haspopup="menu"
                onClick={(e) => ouvrirSous(e.currentTarget, entrees(), { alignement: alignementDuMenu })}
              >
                <MoreHorizontal aria-hidden />
              </Button>
            )}
          </div>
        )}
      </div>
      {menuOuvert}
    </div>
  );
}
